/**
 * Subsection header helpers (#1011): filled-field counts and compact value summaries for subtitles.
 */

import {
  filterSubsectionFieldsForFormView,
  type FormFieldVisibilityContext,
} from './formFieldVisibility';

export type SubsectionFieldDescriptor = {
  /** `formData` / RHF key */
  key: string;
  /** Short label used in dynamic summary (e.g. "Customer") */
  shortLabel: string;
};

function isFilledValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') return !Number.isNaN(value);
  const s = String(value).trim();
  return s.length > 0;
}

export function isSummaryFieldValueFilled(value: unknown): boolean {
  return isFilledValue(value);
}

export function countSubsectionFields(
  fields: readonly SubsectionFieldDescriptor[],
  values: Record<string, unknown>,
): { filled: number; total: number } {
  const total = fields.length;
  const filled = fields.filter(f => isFilledValue(values[f.key])).length;
  return { filled, total };
}

/**
 * Builds a one-line summary from populated fields (e.g. "Customer: Acme · Region: NAM").
 * Returns empty when nothing is filled — caller should fall back to static `titleSubtitle`.
 */
export function buildSubsectionSubtitleSummary(
  fields: readonly SubsectionFieldDescriptor[],
  values: Record<string, unknown>,
  maxSegments = 4,
): string {
  const segments: string[] = [];
  for (const { key, shortLabel } of fields) {
    const raw = values[key];
    if (!isFilledValue(raw)) continue;
    let display: string;
    if (Array.isArray(raw)) {
      display = raw
        .map(v => String(v).trim())
        .filter(Boolean)
        .join(', ');
    } else if (typeof raw === 'boolean') {
      display = raw ? 'Yes' : 'No';
    } else {
      display = String(raw).trim();
    }
    if (!display) continue;
    if (display.length > 48) {
      display = `${display.slice(0, 45)}…`;
    }
    segments.push(`${shortLabel}: ${display}`);
    if (segments.length >= maxSegments) break;
  }
  return segments.join(' · ');
}

/**
 * Maps summary-style rows to header metric field descriptors.
 *
 * **Marker semantics:** the `*SummarySpecs.ts` files set `subsection` only on the *first* row of a
 * section to mark where that section begins; subsequent rows belong to that subsection until the
 * next marker. When `subsection` is provided we walk with running state so every row in that block
 * is included (not just the marker row).
 *
 * When `subsection` is omitted, returns all rows in order (each row's `subsection` marker is
 * ignored — this is the "give me everything" path used by single-card steps).
 */
export function subsectionFieldsFromSummaryRows<
  T extends { dataField: string; label: string; subsection?: string },
>(
  rows: readonly T[],
  subsection?: string,
): readonly SubsectionFieldDescriptor[] {
  let filtered: readonly T[];
  if (subsection === undefined) {
    filtered = rows;
  } else {
    let currentSection: string | undefined;
    filtered = rows.filter(r => {
      if (r.subsection != null) {
        currentSection = r.subsection;
      }
      return currentSection === subsection;
    });
  }
  return filtered.map(({ dataField, label }) => ({
    key: dataField,
    shortLabel: label.length > 22 ? `${label.slice(0, 19)}…` : label,
  }));
}

export function subsectionHeaderMetricsProps(
  fields: readonly SubsectionFieldDescriptor[],
  values: Record<string, unknown>,
  visibility?: FormFieldVisibilityContext,
): {
  fieldFillCount: number;
  fieldTotalCount: number;
  titleSubtitleSummary: string;
} {
  const visibleFields = filterSubsectionFieldsForFormView(fields, visibility);
  const { filled, total } = countSubsectionFields(visibleFields, values);
  return {
    fieldFillCount: filled,
    fieldTotalCount: total,
    titleSubtitleSummary: buildSubsectionSubtitleSummary(visibleFields, values),
  };
}

export type ConfigurationListHeaderMetricsOptions = {
  sharedValues?: Record<string, unknown>;
  /** Request-level keys counted once (not multiplied by unit count). */
  sharedFieldKeys?: readonly string[];
  visibility?: FormFieldVisibilityContext;
};

/**
 * Fill-count badge for multi-unit configuration lists (#997 #4 #10 #11).
 * Sums per-unit fills; shared request fields count once against `sharedValues`.
 */
export function configurationListHeaderMetricsProps(
  metricFields: readonly SubsectionFieldDescriptor[],
  units: readonly Record<string, unknown>[],
  options: ConfigurationListHeaderMetricsOptions = {},
): ReturnType<typeof subsectionHeaderMetricsProps> {
  const { sharedValues = {}, sharedFieldKeys = [], visibility } = options;
  const visibleFields = filterSubsectionFieldsForFormView(
    metricFields,
    visibility,
  );
  if (visibleFields.length === 0) {
    return {
      fieldFillCount: 0,
      fieldTotalCount: 0,
      titleSubtitleSummary: '',
    };
  }

  const sharedKeySet = new Set(sharedFieldKeys);
  const unitFields = visibleFields.filter(
    field => !sharedKeySet.has(field.key),
  );
  const sharedFields = visibleFields.filter(field =>
    sharedKeySet.has(field.key),
  );

  const unitCount = Math.max(units.length, 1);
  let filled = 0;
  let total = unitFields.length * unitCount;

  if (units.length === 0) {
    filled = countSubsectionFields(unitFields, {}).filled;
  } else {
    for (const unit of units) {
      filled += countSubsectionFields(unitFields, unit).filled;
    }
  }

  const sharedCount = countSubsectionFields(sharedFields, sharedValues);
  filled += sharedCount.filled;
  total += sharedCount.total;

  const summaryValues =
    units.length > 0 ? { ...sharedValues, ...units[0] } : sharedValues;

  return {
    fieldFillCount: filled,
    fieldTotalCount: total,
    titleSubtitleSummary: buildSubsectionSubtitleSummary(
      visibleFields,
      summaryValues,
    ),
  };
}
